import { useState, useEffect, useRef, useCallback } from "react";

const API_URL = "https://dogedrop-backend-production.up.railway.app";
const SITE_URL = "https://www.dogedropz.com";

// ============================================================
// CANVAS / PHYSICS CONFIG
// ============================================================
const CANVAS_W = 500;
const CANVAS_H = 560;
const ROWS = 12;
const PEG_R = 5;
const BALL_R = 8;
const PEG_GAP_X = 36;
const PEG_GAP_Y = 38;
const BOARD_TOP = 60;
const GRAVITY = 0.28;
const BOUNCE = 0.55;
const FRICTION = 0.98;

// Slot multipliers — house edge baked in via weights
// Center slots are low, edges are high (classic plinko shape)
const SLOTS = [
  { mult: 10,  color: "#ff003c" },
  { mult: 3,   color: "#ff8c00" },
  { mult: 1.5, color: "#ffe600" },
  { mult: 0.5, color: "#00f0ff" },
  { mult: 0.3, color: "#b44aff" },
  { mult: 0.2, color: "#667788" },
  { mult: 0.3, color: "#b44aff" },
  { mult: 0.5, color: "#00f0ff" },
  { mult: 1.5, color: "#ffe600" },
  { mult: 3,   color: "#ff8c00" },
  { mult: 10,  color: "#ff003c" },
];
const SLOT_W = CANVAS_W / SLOTS.length;

const C = {
  green: "#39ff14", pink: "#ff2d7b", cyan: "#00f0ff", yellow: "#ffe600",
  orange: "#ff8c00", red: "#ff003c", gold: "#c2a633", goldLight: "#e8d56b",
  bg: "#0a0a12", panel: "#12121f", purple: "#b44aff",
};

// ============================================================
// SOUND EFFECTS
// ============================================================
let audioCtx = null;
function getAC() {
  if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  if (audioCtx.state === "suspended") audioCtx.resume();
  return audioCtx;
}

const SFX = {
  peg() {
    try {
      const ctx = getAC(), o = ctx.createOscillator(), g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "sine";
      o.frequency.setValueAtTime(600 + Math.random() * 400, ctx.currentTime);
      o.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + 0.06);
      g.gain.setValueAtTime(0.07, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.07);
      o.start(); o.stop(ctx.currentTime + 0.07);
    } catch {}
  },
  land(mult) {
    try {
      const ctx = getAC();
      const freqs = mult >= 5 ? [523, 659, 784, 1047] : mult >= 1.5 ? [440, 554, 659] : [330, 392];
      freqs.forEach((f, i) => {
        const o = ctx.createOscillator(), g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.type = mult >= 5 ? "sine" : "triangle";
        o.frequency.value = f;
        g.gain.setValueAtTime(0, ctx.currentTime + i * 0.07);
        g.gain.linearRampToValueAtTime(mult >= 1 ? 0.15 : 0.07, ctx.currentTime + i * 0.07 + 0.01);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.07 + 0.22);
        o.start(ctx.currentTime + i * 0.07); o.stop(ctx.currentTime + i * 0.07 + 0.22);
      });
    } catch {}
  },
  drop() {
    try {
      const ctx = getAC(), o = ctx.createOscillator(), g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "sine";
      o.frequency.setValueAtTime(880, ctx.currentTime);
      o.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.12);
      g.gain.setValueAtTime(0.1, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.14);
      o.start(); o.stop(ctx.currentTime + 0.14);
    } catch {}
  },
};

// ============================================================
// PEG LAYOUT
// ============================================================
function buildPegs() {
  const pegs = [];
  for (let row = 0; row < ROWS; row++) {
    const count = row + 3;
    const rowW = (count - 1) * PEG_GAP_X;
    const startX = (CANVAS_W - rowW) / 2;
    for (let col = 0; col < count; col++) {
      pegs.push({
        x: startX + col * PEG_GAP_X,
        y: BOARD_TOP + row * PEG_GAP_Y,
      });
    }
  }
  return pegs;
}

const PEGS = buildPegs();


function createBall(x) {
  return {
    x,
    y: BOARD_TOP - 30,
    dx: (Math.random() - 0.5) * 0.5,
    dy: 0,
    trail: [],
    landed: false,
    slotIndex: -1,
    won: 0,
  };
}

function stepBall(ball, onPegHit, onLand) {
  if (ball.landed) return;

  ball.trail.push({ x: ball.x, y: ball.y });
  if (ball.trail.length > 18) ball.trail.shift();

  ball.dy += GRAVITY;
  ball.dx *= FRICTION;
  ball.x += ball.dx;
  ball.y += ball.dy;

  // Wall bounce — enforce minimum velocity to prevent sticking
  if (ball.x - BALL_R < 0) {
    ball.x = BALL_R + 2;
    ball.dx = Math.max(Math.abs(ball.dx) * BOUNCE, 3.0) + Math.random() * 2.0;
  }
  if (ball.x + BALL_R > CANVAS_W) {
    ball.x = CANVAS_W - BALL_R - 2;
    ball.dx = -(Math.max(Math.abs(ball.dx) * BOUNCE, 3.0) + Math.random() * 2.0);
  }

  // Peg collision
  for (const peg of PEGS) {
    const dx = ball.x - peg.x, dy = ball.y - peg.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const minDist = BALL_R + PEG_R;
    if (dist < minDist && dist > 0.01) {
      const nx = dx / dist, ny = dy / dist;
      ball.x = peg.x + nx * minDist;
      ball.y = peg.y + ny * minDist;
      const dot = ball.dx * nx + ball.dy * ny;
      ball.dx = (ball.dx - 2 * dot * nx) * BOUNCE + (Math.random() - 0.5) * 0.8;
      ball.dy = (ball.dy - 2 * dot * ny) * BOUNCE;
      onPegHit();
    }
  }

  // Landing zone
  const slotY = BOARD_TOP + ROWS * PEG_GAP_Y + 10;
  if (ball.y + BALL_R >= slotY) {
    ball.y = slotY - BALL_R;
    ball.landed = true;
    const idx = Math.min(SLOTS.length - 1, Math.max(0, Math.floor(ball.x / SLOT_W)));
    ball.slotIndex = idx;
    onLand(idx);
  }
}

// ============================================================
// API HELPER
// ============================================================
async function api(path, opts = {}) {
  const token = localStorage.getItem("dd_token");
  const res = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}), ...opts.headers },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "API error");
  return data;
}

// ============================================================
// GLOW TEXT
// ============================================================
function Glow({ children, color = C.green, size = 14, style = {} }) {
  return <span style={{ color, fontSize: size, fontFamily: "'Press Start 2P', monospace", textShadow: `0 0 6px ${color}88, 0 0 18px ${color}44`, ...style }}>{children}</span>;
}

// ============================================================
// LANDING PAGE
// ============================================================
function LandingPage({ onPlay }) {
  const [vis, setVis] = useState([]);
  useEffect(() => {
    [100,300,500,700,900,1100,1300].forEach((d,i) => setTimeout(() => setVis(v=>[...v,i]), d));
  }, []);
  const s = (i) => ({ opacity: vis.includes(i)?1:0, transform: vis.includes(i)?"translateY(0)":"translateY(28px)", transition:"all 0.6s cubic-bezier(0.16,1,0.3,1)" });

  const statCard = (val, label, color) => (
    <div style={{ background:"#12121f", border:`1px solid ${color}22`, borderRadius:12, padding:"20px 14px", textAlign:"center", flex:"1 1 130px" }}>
      <div style={{ fontSize:24, color, fontFamily:"'Press Start 2P',monospace", textShadow:`0 0 20px ${color}66`, marginBottom:8 }}>{val}</div>
      <div style={{ fontSize:8, color:"#888", letterSpacing:2 }}>{label}</div>
    </div>
  );

  const stepCard = (num, title, desc, color) => (
    <div style={{ background:"#12121f", border:"1px solid #ffffff08", borderRadius:12, padding:20, display:"flex", gap:16, alignItems:"flex-start" }}>
      <div style={{ width:38, height:38, borderRadius:"50%", background:`${color}22`, border:`2px solid ${color}44`, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Press Start 2P',monospace", fontSize:13, color, flexShrink:0 }}>{num}</div>
      <div>
        <div style={{ fontSize:12, color, fontFamily:"'Chakra Petch',sans-serif", fontWeight:700, marginBottom:6 }}>{title}</div>
        <div style={{ fontSize:12, color:"#888", lineHeight:1.7, fontFamily:"'Chakra Petch',sans-serif" }}>{desc}</div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight:"100vh", background:C.bg, color:"#fff", fontFamily:"'Chakra Petch','Courier New',sans-serif", overflowX:"hidden" }}>
      <link href="https://fonts.googleapis.com/css2?family=Press+Start+2P&family=Chakra+Petch:wght@400;600;700&display=swap" rel="stylesheet" />
      <div style={{ position:"fixed", inset:0, background:`radial-gradient(ellipse at 50% 0%, #1a1a3a 0%, transparent 60%), radial-gradient(ellipse at 80% 100%, ${C.gold}08 0%, transparent 40%)`, pointerEvents:"none", zIndex:0 }} />
      <div style={{ position:"fixed", inset:0, backgroundImage:`linear-gradient(#ffffff05 1px, transparent 1px), linear-gradient(90deg, #ffffff05 1px, transparent 1px)`, backgroundSize:"60px 60px", pointerEvents:"none", zIndex:0 }} />

      <div style={{ position:"relative", zIndex:1, maxWidth:800, margin:"0 auto", padding:"0 20px" }}>

        {/* Hero */}
        <div style={{ textAlign:"center", paddingTop:80, paddingBottom:60, ...s(0) }}>
          <a href="https://stakedape.com" target="_blank" style={{ textDecoration:"none", display:"inline-block", marginBottom:16 }}>
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:0 }}>
              <span style={{ fontSize:36, lineHeight:1.2 }}>🦧</span>
              <div style={{ fontFamily:"'Press Start 2P',monospace", fontSize:12, color:"#fff", letterSpacing:4, lineHeight:1.4 }}>STAKED</div>
              <div style={{ fontFamily:"'Press Start 2P',monospace", fontSize:12, color:"#39ff14", letterSpacing:4, textShadow:"0 0 14px #39ff1488" }}>APE</div>
            </div>
          </a>
          <div style={{ width:80, height:80, borderRadius:"50%", background:"radial-gradient(circle at 38% 35%, #f5e17a, #c2a633 55%, #8a6e1a)", boxShadow:"0 0 40px #c2a63388", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 20px", border:"3px solid #e8d56b" }}>
            <span style={{ fontFamily:"Arial Black,sans-serif", fontWeight:900, fontSize:42, color:"#8a6e1a", lineHeight:1 }}>Ð</span>
          </div>
          <h1 style={{ fontSize:"clamp(18px, 8vw, 42px)", margin:0, color:C.gold, letterSpacing:8, fontFamily:"'Press Start 2P',monospace", textShadow:`0 0 30px ${C.gold}44, 0 0 80px ${C.gold}11`, lineHeight:1.2 }}>DOGEDROPZ</h1>
          <p style={{ fontSize:16, color:C.cyan, marginTop:16, letterSpacing:6, fontWeight:600 }}>DROP THE COIN. WIN DOGE.</p>
          <p style={{ fontSize:14, color:"#888", marginTop:20, maxWidth:500, margin:"20px auto 0", lineHeight:1.8 }}>
            The crypto plinko game that pays real Dogecoin. Drop the coin, watch it bounce through the pegs, and land on a multiplier. Cash out instantly.
          </p>
          <div style={{ marginTop:32 }}>
            <button onClick={onPlay} style={{ padding:"18px 48px", fontSize:16, fontFamily:"'Press Start 2P',monospace", color:C.bg, fontWeight:"bold", background:`linear-gradient(135deg, ${C.gold}, ${C.goldLight})`, border:"none", borderRadius:12, cursor:"pointer", letterSpacing:4, boxShadow:`0 4px 30px ${C.gold}44`, transition:"transform 0.2s" }}
              onMouseEnter={e=>e.target.style.transform="scale(1.05)"} onMouseLeave={e=>e.target.style.transform="scale(1)"}>
              PLAY NOW
            </button>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display:"flex", gap:12, flexWrap:"wrap", justifyContent:"center", marginBottom:60, ...s(1) }}>
          {statCard("10x", "MAX MULTIPLIER", C.red)}
          {statCard("5%", "REFERRAL BONUS", C.purple)}
          {statCard("2%", "COMMISSION", C.cyan)}
          {statCard("DOGE", "INSTANT PAYOUT", C.green)}
        </div>

        {/* Multipliers */}
        <div style={{ marginBottom:60, ...s(2) }}>
          <div style={{ background:"#12121f", border:`1px solid ${C.gold}22`, borderRadius:16, padding:32, textAlign:"center" }}>
            <h2 style={{ fontSize:16, color:C.gold, fontFamily:"'Press Start 2P',monospace", marginBottom:16, letterSpacing:3 }}>THE BOARD</h2>
            <p style={{ fontSize:13, color:"#ccc", lineHeight:1.8, maxWidth:500, margin:"0 auto 20px" }}>
              12 rows of pegs with real physics. The coin bounces randomly — land on the edges for big wins, center for steady returns.
            </p>
            <div style={{ display:"flex", gap:6, justifyContent:"center", flexWrap:"wrap" }}>
              {[["10x","#ff003c"],["3x","#ff8c00"],["1.5x","#ffe600"],["0.5x","#00f0ff"],["0.3x","#b44aff"],["0.2x","#667788"]].map(([label,color])=>(
                <div key={label} style={{ padding:"6px 14px", borderRadius:6, background:`${color}15`, border:`1px solid ${color}33`, fontSize:12, color, fontFamily:"'Press Start 2P',monospace" }}>{label}</div>
              ))}
            </div>
          </div>
        </div>

        {/* How to play */}
        <div style={{ marginBottom:60, ...s(3) }}>
          <h2 style={{ fontSize:16, color:C.gold, fontFamily:"'Press Start 2P',monospace", textAlign:"center", marginBottom:28, letterSpacing:4 }}>HOW TO PLAY</h2>
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {stepCard("1","CONNECT YOUR WALLET","Paste your Dogecoin address and create a PIN. No extensions or browser wallets needed.", C.gold)}
            {stepCard("2","DEPOSIT DOGE","Send DOGE to your deposit address. Click Check for Deposits and your balance updates automatically.", C.cyan)}
            {stepCard("3","AIM & DROP","Move your mouse to aim, then click DROP or press spacebar. Watch the coin bounce through the pegs.", C.green)}
            {stepCard("4","WIN & WITHDRAW","Land on a multiplier and your winnings are credited instantly. Withdraw anytime directly to your wallet.", C.pink)}
          </div>
        </div>

        {/* Referral */}
        <div style={{ marginBottom:60, ...s(4) }}>
          <h2 style={{ fontSize:16, color:C.purple, fontFamily:"'Press Start 2P',monospace", textAlign:"center", marginBottom:28, letterSpacing:4 }}>EARN PASSIVE DOGE</h2>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(200px, 1fr))", gap:16 }}>
            <div style={{ background:"#12121f", border:`1px solid ${C.purple}22`, borderRadius:12, padding:24, textAlign:"center" }}>
              <div style={{ fontSize:28, marginBottom:12 }}>🔗</div>
              <div style={{ fontSize:12, color:C.purple, fontWeight:700, marginBottom:8 }}>SHARE YOUR LINK</div>
              <div style={{ fontSize:12, color:"#888", lineHeight:1.7 }}>Every player gets a unique referral link to share anywhere.</div>
            </div>
            <div style={{ background:"#12121f", border:`1px solid ${C.green}22`, borderRadius:12, padding:24, textAlign:"center" }}>
              <div style={{ fontSize:28, marginBottom:12 }}>💰</div>
              <div style={{ fontSize:12, color:C.green, fontWeight:700, marginBottom:8 }}>5% DEPOSIT BONUS</div>
              <div style={{ fontSize:12, color:"#888", lineHeight:1.7 }}>Earn 5% of your referral's first deposit instantly.</div>
            </div>
            <div style={{ background:"#12121f", border:`1px solid ${C.cyan}22`, borderRadius:12, padding:24, textAlign:"center" }}>
              <div style={{ fontSize:28, marginBottom:12 }}>♾️</div>
              <div style={{ fontSize:12, color:C.cyan, fontWeight:700, marginBottom:8 }}>2% FOREVER</div>
              <div style={{ fontSize:12, color:"#888", lineHeight:1.7 }}>Earn 2% of every drop your referrals make, forever.</div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div style={{ textAlign:"center", paddingBottom:80, ...s(5) }}>
          <div style={{ background:`linear-gradient(135deg, ${C.gold}11, ${C.panel})`, border:`1px solid ${C.gold}22`, borderRadius:20, padding:"40px 24px" }}>
            <div style={{ width:56, height:56, borderRadius:"50%", background:"radial-gradient(circle at 38% 35%, #f5e17a, #c2a633 55%, #8a6e1a)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px", border:"2px solid #e8d56b" }}>
              <span style={{ fontFamily:"Arial Black,sans-serif", fontWeight:900, fontSize:30, color:"#8a6e1a" }}>Ð</span>
            </div>
            <h2 style={{ fontSize:16, color:C.gold, fontFamily:"'Press Start 2P',monospace", marginBottom:12, letterSpacing:3 }}>READY TO DROP?</h2>
            <p style={{ fontSize:13, color:"#888", marginBottom:24, lineHeight:1.7 }}>Connect your Dogecoin wallet, deposit DOGE, and start dropping.</p>
            <button onClick={onPlay} style={{ padding:"16px 40px", fontSize:14, fontFamily:"'Press Start 2P',monospace", color:C.bg, background:`linear-gradient(135deg, ${C.gold}, ${C.goldLight})`, border:"none", borderRadius:10, cursor:"pointer", letterSpacing:4, boxShadow:`0 4px 30px ${C.gold}44` }}>START DROPPING</button>
          </div>
        </div>

        <div style={{ textAlign:"center", paddingBottom:30, paddingTop:10, borderTop:"1px solid #1a1a2e" }}>
          <a href="https://stakedape.com" target="_blank" style={{ textDecoration:"none", display:"inline-flex", alignItems:"center", gap:6, marginBottom:8 }}>
            <span style={{ fontSize:18 }}>🦧</span>
            <span style={{ fontFamily:"'Press Start 2P',monospace", fontSize:8, color:"#fff", letterSpacing:3 }}>STAKED</span>
            <span style={{ fontFamily:"'Press Start 2P',monospace", fontSize:8, color:"#39ff14", letterSpacing:3, textShadow:"0 0 8px #39ff1433" }}>APE</span>
          </a>
          <div style={{ fontSize:9, color:"#333", letterSpacing:2, marginBottom:4 }}>DOGEDROPZ · DOGEBRIX · DOGEGEMZ</div>
          <div style={{ fontSize:9, color:"#222" }}>for entertainment purposes · 18+</div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// CONNECT SCREEN
// ============================================================
function ConnectScreen({ onConnect, onBack }) {
  const [address, setAddress] = useState("");
  const [pin, setPin] = useState("");
  const [pinConfirm, setPinConfirm] = useState("");
  const [isNew, setIsNew] = useState(false);
  const [step, setStep] = useState("address");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const checkAddr = async () => {
    const t = address.trim();
    if (!t || t.length < 20) { setError("Enter a valid Dogecoin address"); return; }
    setLoading(true); setError("");
    try { const d = await api("/api/auth/check", { method: "POST", body: { walletAddress: t } }); setIsNew(!d.exists || !d.hasPin); setStep("pin"); }
    catch (e) { setError(e.message); }
    setLoading(false);
  };

  const connect = async () => {
    if (!pin || pin.length < 6) { setError("PIN must be at least 6 characters"); return; }
    if (isNew && pin !== pinConfirm) { setError("PINs don't match"); return; }
    setLoading(true); setError("");
    try {
      const d = await api("/api/auth/connect", { method: "POST", body: { walletAddress: address.trim(), pin } });
      localStorage.setItem("dd_token", d.token);
      onConnect(d.user);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const inp = { width: "100%", padding: "12px 14px", background: "#0a0a16", border: `1px solid ${error ? C.red : "#333"}`, borderRadius: 6, color: "#fff", fontFamily: "monospace", fontSize: 11, boxSizing: "border-box", outline: "none" };

  return (
    <div style={{ minHeight: "100vh", background: `radial-gradient(ellipse at 50% 0%, #1a1a3a 0%, ${C.bg} 70%)`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "'Press Start 2P', monospace", color: "#fff", padding: 20 }}>
      <link href="https://fonts.googleapis.com/css2?family=Press+Start+2P&display=swap" rel="stylesheet" />
      <div style={{ textAlign: "center", marginBottom: 36 }}>
        <div style={{ width: 64, height: 64, borderRadius: "50%", background: "radial-gradient(circle at 38% 35%, #f5e17a, #c2a633 55%, #8a6e1a)", boxShadow: "0 0 24px #c2a63388, 2px 3px 8px #0008", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px", border: "3px solid #e8d56b" }}>
          <span style={{ fontFamily: "Arial Black, sans-serif", fontWeight: 900, fontSize: 32, color: "#8a6e1a", textShadow: "0 1px 2px #c2a63388", lineHeight: 1 }}>Ð</span>
        </div>
        <h1 style={{ fontSize: 28, margin: 0, color: C.gold, letterSpacing: 6, textShadow: `0 0 20px ${C.gold}66` }}>DOGEDROPZ</h1>
        <div style={{ fontSize: 8, color: C.cyan, marginTop: 8, letterSpacing: 4 }}>DROP THE COIN. WIN DOGE.</div>
      </div>
      <div style={{ background: C.panel, border: `1px solid ${C.gold}33`, borderRadius: 16, padding: 28, width: "100%", maxWidth: 400 }}>
        {step === "address" ? (
          <>
            <div style={{ fontSize: 9, color: C.gold, marginBottom: 14, textAlign: "center", letterSpacing: 2 }}>CONNECT WALLET</div>
            <input type="text" value={address} onChange={e => setAddress(e.target.value)} placeholder="Your DOGE address..." style={inp} onKeyDown={e => e.key === "Enter" && checkAddr()} />
            {error && <div style={{ fontSize: 7, color: C.red, marginTop: 6 }}>{error}</div>}
            <button onClick={checkAddr} disabled={loading} style={{ width: "100%", marginTop: 14, padding: "13px 0", background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`, border: "none", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: 10, color: C.bg, fontWeight: "bold", letterSpacing: 3 }}>{loading ? "CHECKING..." : "CONTINUE"}</button>
            <button onClick={onBack} style={{ width: "100%", marginTop: 8, padding: "9px 0", background: "transparent", border: "1px solid #333", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: 8, color: "#555" }}>BACK TO HOME</button>
          </>
        ) : (
          <>
            <div style={{ fontSize: 9, color: C.gold, marginBottom: 12, textAlign: "center" }}>{isNew ? "CREATE PIN" : "ENTER PIN"}</div>
            <div style={{ fontSize: 7, color: C.green, fontFamily: "monospace", marginBottom: 12, textAlign: "center" }}>{address.slice(0, 8)}...{address.slice(-6)}</div>
            <input type="password" value={pin} onChange={e => setPin(e.target.value)} placeholder="PIN (6+ chars)" style={{ ...inp, marginBottom: isNew ? 10 : 0 }} onKeyDown={e => e.key === "Enter" && !isNew && connect()} />
            {isNew && <input type="password" value={pinConfirm} onChange={e => setPinConfirm(e.target.value)} placeholder="Confirm PIN" style={inp} onKeyDown={e => e.key === "Enter" && connect()} />}
            {error && <div style={{ fontSize: 7, color: C.red, marginTop: 6 }}>{error}</div>}
            <button onClick={connect} disabled={loading} style={{ width: "100%", marginTop: 14, padding: "13px 0", background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`, border: "none", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: 10, color: C.bg, fontWeight: "bold", letterSpacing: 3 }}>{loading ? "CONNECTING..." : "CONNECT"}</button>
            <button onClick={() => { setStep("address"); setPin(""); setPinConfirm(""); setError(""); }} style={{ width: "100%", marginTop: 8, padding: "9px 0", background: "transparent", border: "1px solid #333", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: 8, color: "#555" }}>BACK</button>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================
// WALLET PANEL
// ============================================================
function WalletPanel({ user, balance, onBalanceUpdate, onClose, onDisconnect, houseWallet }) {
  const [tab, setTab] = useState("deposit");
  const [amount, setAmount] = useState("");
  const [copied, setCopied] = useState(false);
  const [refCopied, setRefCopied] = useState(false);
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(false);
  const [refStats, setRefStats] = useState(null);

  useEffect(() => { api("/api/referral/stats").then(setRefStats).catch(() => {}); }, []);

  const copyAddr = () => { navigator.clipboard.writeText(houseWallet); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  const copyRef = () => { navigator.clipboard.writeText(`${SITE_URL}?ref=${user.referralCode}`); setRefCopied(true); setTimeout(() => setRefCopied(false), 2000); };

  const checkDeposits = async () => {
    setChecking(true); setStatus({ t: "i", m: "Checking blockchain..." });
    try {
      const d = await api("/api/wallet/deposit/check", { method: "POST" });
      onBalanceUpdate(d.balance);
      setStatus(d.credited > 0 ? { t: "s", m: `${d.credited.toFixed(2)} DOGE credited!` } : { t: "i", m: "No new deposits found." });
    } catch (e) { setStatus({ t: "e", m: e.message }); }
    setChecking(false);
  };

  const withdraw = async () => {
    const v = parseFloat(amount);
    if (isNaN(v) || v < 10) { setStatus({ t: "e", m: "Minimum: 10 DOGE" }); return; }
    try {
      const d = await api("/api/wallet/withdraw", { method: "POST", body: { amount: v } });
      onBalanceUpdate(d.balance);
      setStatus({ t: "s", m: `${v} DOGE withdrawal submitted` });
      setAmount("");
    } catch (e) { setStatus({ t: "e", m: e.message }); }
  };

  const tabBtn = (active) => ({ flex: 1, padding: "9px 0", textAlign: "center", cursor: "pointer", fontSize: 7, fontFamily: "inherit", letterSpacing: 2, border: "none", background: active ? C.gold + "22" : "transparent", borderBottom: active ? `2px solid ${C.gold}` : "2px solid transparent", color: active ? C.gold : "#555" });
  const inp = { width: "100%", padding: "9px 12px", background: "#0a0a16", border: "1px solid #333", borderRadius: 4, color: "#fff", fontFamily: "monospace", fontSize: 10, boxSizing: "border-box", outline: "none" };
  const stColor = status?.t === "s" ? C.green : status?.t === "e" ? C.red : C.cyan;

  return (
    <div style={{ position: "fixed", inset: 0, background: "#000000cc", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: C.panel, border: `1px solid ${C.gold}33`, borderRadius: 16, width: "100%", maxWidth: 420, maxHeight: "88vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderBottom: "1px solid #ffffff11" }}>
          <Glow color={C.gold} size={10}>WALLET</Glow>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#555", cursor: "pointer", fontSize: 16 }}>✕</button>
        </div>
        <div style={{ padding: "10px 16px", borderBottom: "1px solid #ffffff08", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 6, color: "#444", marginBottom: 2 }}>{user.verified ? "✅ VERIFIED" : "⚠️ UNVERIFIED"}</div>
            <div style={{ fontSize: 7, color: C.green, fontFamily: "monospace" }}>{user.walletAddress.slice(0, 8)}...{user.walletAddress.slice(-6)}</div>
          </div>
          <button onClick={onDisconnect} style={{ padding: "5px 10px", fontSize: 6, fontFamily: "inherit", background: C.red + "11", border: `1px solid ${C.red}33`, color: C.red, borderRadius: 4, cursor: "pointer" }}>DISCONNECT</button>
        </div>
        <div style={{ padding: "12px 16px", textAlign: "center", borderBottom: "1px solid #ffffff08" }}>
          <div style={{ fontSize: 6, color: "#555", marginBottom: 4 }}>BALANCE</div>
          <Glow color={C.gold} size={18}>{balance.toFixed(2)} DOGE</Glow>
        </div>
        <div style={{ display: "flex", borderBottom: "1px solid #ffffff11" }}>
          <button onClick={() => { setTab("deposit"); setStatus(null); }} style={tabBtn(tab === "deposit")}>DEPOSIT</button>
          <button onClick={() => { setTab("withdraw"); setStatus(null); }} style={tabBtn(tab === "withdraw")}>WITHDRAW</button>
          <button onClick={() => { setTab("referral"); setStatus(null); }} style={tabBtn(tab === "referral")}>REFERRAL</button>
        </div>
        <div style={{ padding: 16 }}>
          {tab === "deposit" && (
            <>
              <div style={{ background: "#0a0a16", border: `1px solid ${C.gold}33`, borderRadius: 8, padding: 12, marginBottom: 12, wordBreak: "break-all" }}>
                <div style={{ fontSize: 6, color: "#555", marginBottom: 6 }}>SEND DOGE TO</div>
                <div style={{ fontSize: 7, color: C.goldLight, lineHeight: 1.6, marginBottom: 8 }}>{houseWallet}</div>
                <button onClick={copyAddr} style={{ padding: "6px 14px", fontSize: 7, fontFamily: "inherit", borderRadius: 4, cursor: "pointer", background: copied ? C.green + "22" : C.gold + "22", border: `1px solid ${copied ? C.green : C.gold}44`, color: copied ? C.green : C.gold }}>{copied ? "COPIED ✓" : "COPY ADDRESS"}</button>
              </div>
              <button onClick={checkDeposits} disabled={checking} style={{ width: "100%", padding: "11px 0", background: checking ? "#333" : `linear-gradient(135deg, ${C.cyan}, ${C.green})`, border: "none", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: 9, color: C.bg, fontWeight: "bold", letterSpacing: 2 }}>{checking ? "CHECKING..." : "CHECK FOR DEPOSITS"}</button>
            </>
          )}
          {tab === "withdraw" && (
            <>
              {!user.verified && <div style={{ fontSize: 7, color: C.red, marginBottom: 8 }}>Deposit first to enable withdrawals.</div>}
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Amount (min 10)" style={inp} />
              <button onClick={withdraw} disabled={!user.verified} style={{ width: "100%", marginTop: 10, padding: "11px 0", background: user.verified ? `linear-gradient(135deg, ${C.cyan}, ${C.green})` : "#333", border: "none", borderRadius: 8, cursor: user.verified ? "pointer" : "not-allowed", fontFamily: "inherit", fontSize: 9, color: C.bg, fontWeight: "bold", letterSpacing: 2 }}>WITHDRAW</button>
            </>
          )}
          {status && <div style={{ marginTop: 10, padding: 8, borderRadius: 4, textAlign: "center", fontSize: 7, background: stColor + "11", border: `1px solid ${stColor}33`, color: stColor }}>{status.m}</div>}
          {tab === "referral" && (
            <>
              <div style={{ fontSize: 7, color: "#888", marginBottom: 12, lineHeight: 1.8 }}>Share your link. Earn 5% of their first deposit + 2% of every drop they make.</div>
              <div style={{ background: "#0a0a16", border: `1px solid ${C.purple}33`, borderRadius: 8, padding: 12, marginBottom: 12 }}>
                <div style={{ fontSize: 6, color: "#555", marginBottom: 6 }}>YOUR REFERRAL LINK</div>
                <div style={{ fontSize: 7, color: C.purple, lineHeight: 1.6, marginBottom: 8, wordBreak: "break-all" }}>{SITE_URL}?ref={user.referralCode}</div>
                <button onClick={copyRef} style={{ padding: "6px 14px", fontSize: 7, fontFamily: "inherit", borderRadius: 4, cursor: "pointer", background: refCopied ? C.green + "22" : C.purple + "22", border: `1px solid ${refCopied ? C.green : C.purple}44`, color: refCopied ? C.green : C.purple }}>{refCopied ? "COPIED ✓" : "COPY LINK"}</button>
              </div>
              {refStats && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
                  <div style={{ background: "#0a0a16", borderRadius: 6, padding: 10, textAlign: "center" }}><div style={{ fontSize: 6, color: "#555", marginBottom: 4 }}>REFERRALS</div><Glow color={C.cyan} size={14}>{refStats.referrals}</Glow></div>
                  <div style={{ background: "#0a0a16", borderRadius: 6, padding: 10, textAlign: "center" }}><div style={{ fontSize: 6, color: "#555", marginBottom: 4 }}>EARNED</div><Glow color={C.green} size={14}>{refStats.totalEarned.toFixed(2)}</Glow></div>
                </div>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`I just won DOGE playing DOGEDROPZ! Drop the coin, win crypto. ${SITE_URL}?ref=${user.referralCode}`)}`, "_blank")} style={{ flex: 1, padding: "10px 0", fontSize: 7, fontFamily: "inherit", background: "#1DA1F222", border: "1px solid #1DA1F244", color: "#1DA1F2", borderRadius: 6, cursor: "pointer" }}>SHARE ON X</button>
                <button onClick={() => window.open(`https://t.me/share/url?url=${encodeURIComponent(`${SITE_URL}?ref=${user.referralCode}`)}&text=${encodeURIComponent("I just won DOGE playing DOGEDROPZ! Drop the coin, win crypto.")}`, "_blank")} style={{ flex: 1, padding: "10px 0", fontSize: 7, fontFamily: "inherit", background: "#0088cc22", border: "1px solid #0088cc44", color: "#0088cc", borderRadius: 6, cursor: "pointer" }}>TELEGRAM</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MAIN APP
// ============================================================
export default function App() {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const ballsRef = useRef([]);
  const pegHitTimers = useRef([]);
  const dogeImgRef = useRef(null);

  // Preload dogecoin image
  // No image preload needed — coin drawn directly on canvas

  const [screen, setScreen] = useState("loading");
  const [user, setUser] = useState(null);
  const [balance, setBalance] = useState(0);
  const [betAmount, setBetAmount] = useState(10);
  const [activeBalls, setActiveBalls] = useState(0);
  const [result, setResult] = useState(null);   // { mult, won, slotIndex }
  const [history, setHistory] = useState([]);
  const [walletOpen, setWalletOpen] = useState(false);
  const [houseWallet, setHouseWallet] = useState("");
  const [message, setMessage] = useState("");
  const [flashSlot, setFlashSlot] = useState(-1);

  // ── Auth init ────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try { const i = await api("/api/info"); setHouseWallet(i.houseWallet); } catch {}
      const t = localStorage.getItem("dd_token");
      if (t) {
        try { const u = await api("/api/auth/me"); setUser(u); setBalance(u.balance); setScreen("game"); return; }
        catch { localStorage.removeItem("dd_token"); }
      }
      setScreen("landing");
    })();
  }, []);

  const handleConnect = (u) => { setUser(u); setBalance(u.balance); setScreen("game"); };
  const handleDisconnect = () => { localStorage.removeItem("dd_token"); setUser(null); setBalance(0); setScreen("landing"); };

  // ── Drawing ──────────────────────────────────────────────
  const draw = useCallback(() => {
    const cv = canvasRef.current; if (!cv) return;
    const ctx = cv.getContext("2d");
    const now = Date.now();

    // Background
    ctx.fillStyle = C.bg; ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    // Grid
    ctx.strokeStyle = "#1a1a2e"; ctx.lineWidth = 1;
    for (let i = 0; i < CANVAS_W; i += 30) { ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i, CANVAS_H); ctx.stroke(); }
    for (let i = 0; i < CANVAS_H; i += 30) { ctx.beginPath(); ctx.moveTo(0, i); ctx.lineTo(CANVAS_W, i); ctx.stroke(); }

    // Pegs
    for (const peg of PEGS) {
      const hit = pegHitTimers.current.find(h => h.x === peg.x && h.y === peg.y);
      const glow = hit ? Math.max(0, 1 - (now - hit.t) / 150) : 0;
      ctx.beginPath(); ctx.arc(peg.x, peg.y, PEG_R, 0, Math.PI * 2);
      ctx.fillStyle = glow > 0 ? `rgba(200,170,51,${0.4 + glow * 0.6})` : "#3a3020";
      ctx.shadowBlur = glow * 12; ctx.shadowColor = C.gold;
      ctx.fill(); ctx.shadowBlur = 0;
    }
    // Clean up old peg hits
    pegHitTimers.current = pegHitTimers.current.filter(h => now - h.t < 200);

    // Slot dividers + labels
    const slotY = BOARD_TOP + ROWS * PEG_GAP_Y + 10;
    for (let i = 0; i < SLOTS.length; i++) {
      const x = i * SLOT_W, slot = SLOTS[i];
      const isFlash = flashSlot === i;
      ctx.fillStyle = isFlash ? slot.color + "55" : slot.color + "18";
      ctx.fillRect(x + 1, slotY, SLOT_W - 2, CANVAS_H - slotY);
      if (isFlash) { ctx.shadowBlur = 20; ctx.shadowColor = slot.color; }
      ctx.fillStyle = isFlash ? slot.color : slot.color + "cc";
      ctx.font = `bold ${SLOT_W > 40 ? 9 : 7}px monospace`;
      ctx.textAlign = "center";
      ctx.fillText(`${slot.mult}x`, x + SLOT_W / 2, slotY + 18);
      ctx.shadowBlur = 0;
      // Divider
      ctx.strokeStyle = slot.color + "44"; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(x, slotY); ctx.lineTo(x, CANVAS_H); ctx.stroke();
    }

    // Draw all active balls
    const balls = ballsRef.current;
    for (const ball of balls) {
      if (ball.landed) continue;
      for (let i = 0; i < ball.trail.length; i++) {
        const t = ball.trail[i];
        const alpha = (i / ball.trail.length) * 0.35;
        ctx.beginPath(); ctx.arc(t.x, t.y, BALL_R * 0.6, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(194,166,51,${alpha})`; ctx.fill();
      }
      ctx.save();
      ctx.beginPath(); ctx.arc(ball.x, ball.y, BALL_R, 0, Math.PI * 2);
      const coinGrad = ctx.createRadialGradient(ball.x - BALL_R * 0.3, ball.y - BALL_R * 0.3, 1, ball.x, ball.y, BALL_R);
      coinGrad.addColorStop(0, "#f5e17a"); coinGrad.addColorStop(0.55, "#c2a633"); coinGrad.addColorStop(1, "#8a6e1a");
      ctx.fillStyle = coinGrad; ctx.shadowBlur = 16; ctx.shadowColor = C.gold;
      ctx.fill(); ctx.shadowBlur = 0;
      ctx.strokeStyle = "#e8d56b"; ctx.lineWidth = 1.2; ctx.stroke();
      ctx.clip();
      ctx.fillStyle = "#8a6e1a";
      ctx.font = `bold ${BALL_R * 1.3}px Arial Black, sans-serif`;
      ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.fillText("Ð", ball.x, ball.y + 0.5);
      ctx.restore();
    }

    // Idle prompt
    if (balls.length === 0) {
      ctx.fillStyle = C.gold + "55"; ctx.font = "bold 11px 'Press Start 2P', monospace"; ctx.textAlign = "center";
      ctx.fillText("DROP THE COIN", CANVAS_W / 2, CANVAS_H / 2);
      ctx.font = "8px monospace"; ctx.fillStyle = "#444";
      ctx.fillText("click to aim, press DROP to play", CANVAS_W / 2, CANVAS_H / 2 + 22);
    }
  }, [activeBalls, flashSlot]);

  // ── Aim line on canvas click ─────────────────────────────
  const [aimX, setAimX] = useState(CANVAS_W / 2);
  useEffect(() => {
    const cv = canvasRef.current; if (!cv) return;
    const onMove = (e) => {
      const r = cv.getBoundingClientRect();
      const cx = e.touches ? e.touches[0].clientX : e.clientX;
      setAimX(Math.max(BALL_R, Math.min(CANVAS_W - BALL_R, (cx - r.left) * (CANVAS_W / r.width))));
    };
    cv.addEventListener("mousemove", onMove);
    cv.addEventListener("touchmove", onMove, { passive: true });
    return () => { cv.removeEventListener("mousemove", onMove); cv.removeEventListener("touchmove", onMove); };
  }, []);

  // ── Aim indicator overlay ────────────────────────────────
  useEffect(() => {
    const cv = canvasRef.current; if (!cv) return;
    const ctx = cv.getContext("2d");
    draw();
    // Aim line
    ctx.strokeStyle = C.gold + "55"; ctx.lineWidth = 1; ctx.setLineDash([4, 4]);
    ctx.beginPath(); ctx.moveTo(aimX, 0); ctx.lineTo(aimX, BOARD_TOP - 30); ctx.stroke();
    ctx.setLineDash([]);
    // Aim ball ghost
    ctx.beginPath(); ctx.arc(aimX, BOARD_TOP - 30, BALL_R, 0, Math.PI * 2);
    ctx.fillStyle = C.gold + "44"; ctx.fill();
  }, [aimX, draw]);

  // ── Game loop — runs continuously while any ball is active ──
  useEffect(() => {
    if (activeBalls === 0) return;
    let stopped = false;

    const loop = () => {
      if (stopped) return;
      const balls = ballsRef.current;
      const stillActive = balls.filter(b => !b.landed);
      if (stillActive.length === 0) return;

      for (const ball of stillActive) {
        stepBall(
          ball,
          () => {
            pegHitTimers.current.push({ x: ball.x, y: ball.y, t: Date.now() });
            SFX.peg();
          },
          (slotIndex) => {
            const mult = SLOTS[slotIndex].mult;
            const won = +(ball.betAmount * mult).toFixed(4);
            ball.won = won;
            SFX.land(mult);
            setFlashSlot(slotIndex);
            setTimeout(() => setFlashSlot(-1), 800);
            api("/api/plinko/drop", { method: "POST", body: { betAmount: ball.betAmount, slotIndex, mult, won } })
              .then(d => {
                setBalance(d.balance);
                setResult({ mult, won: d.won, slotIndex });
                setHistory(h => [{ mult, won: d.won, bet: ball.betAmount, time: Date.now() }, ...h].slice(0, 20));
                setMessage(mult >= 5 ? `🎉 ${mult}x — WON ${d.won.toFixed(2)} DOGE!` : mult >= 1 ? `+${d.won.toFixed(2)} DOGE` : `${mult}x — ${d.won.toFixed(2)} DOGE`);
              })
              .catch(e => { setMessage("❌ " + e.message); })
              .finally(() => {
                ballsRef.current = ballsRef.current.filter(b => b !== ball);
                setActiveBalls(ballsRef.current.length);
              });
          }
        );
      }

      draw();
      animRef.current = requestAnimationFrame(loop);
    };

    animRef.current = requestAnimationFrame(loop);
    return () => { stopped = true; cancelAnimationFrame(animRef.current); };
  }, [activeBalls, draw]);

  // ── Drop — allow multiple simultaneous balls ─────────────
  const dropBall = useCallback(async () => {
    if (balance < betAmount) return;
    setResult(null); setMessage(""); setFlashSlot(-1);
    setBalance(b => +(b - betAmount).toFixed(4));
    const ball = createBall(aimX);
    ball.betAmount = betAmount;
    ballsRef.current = [...ballsRef.current, ball];
    SFX.drop();
    setActiveBalls(ballsRef.current.length);
  }, [balance, betAmount, aimX]);

  // ── Draw idle ────────────────────────────────────────────
  useEffect(() => {
    if (activeBalls === 0) draw();
  }, [draw, activeBalls]);

  // ── Keyboard drop ────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => { if (e.code === "Space" || e.key === " ") { e.preventDefault(); dropBall(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dropBall]);

  // ============================================================
  // RENDER
  // ============================================================
  if (screen === "loading") return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Press Start 2P', monospace", color: C.gold }}>
      <link href="https://fonts.googleapis.com/css2?family=Press+Start+2P&display=swap" rel="stylesheet" />
      Loading...
    </div>
  );

  if (screen === "landing") return <LandingPage onPlay={() => setScreen("connect")} />;
  if (screen === "connect") return <ConnectScreen onConnect={handleConnect} onBack={() => setScreen("landing")} />;

  const canDrop = balance >= betAmount && balance > 0;
  const bets = [10, 25, 50, 100, 250, 500, 1000];

  return (
    <div style={{ minHeight: "100vh", background: `radial-gradient(ellipse at 50% 0%, #1a1a3a 0%, ${C.bg} 70%)`, display: "flex", flexDirection: "column", alignItems: "center", fontFamily: "'Press Start 2P', 'Courier New', monospace", color: "#fff", padding: "12px 8px" }}>
      <link href="https://fonts.googleapis.com/css2?family=Press+Start+2P&family=Chakra+Petch:wght@400;600;700&display=swap" rel="stylesheet" />

      {walletOpen && <WalletPanel user={user} balance={balance} onBalanceUpdate={setBalance} onClose={() => setWalletOpen(false)} onDisconnect={handleDisconnect} houseWallet={houseWallet} />}

      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 10 }}>
        <a href="https://stakedape.com" target="_blank" style={{ textDecoration: "none", display: "inline-block", marginBottom: 6 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0 }}>
            <span style={{ fontSize: 28, lineHeight: 1.2 }}>🦧</span>
            <div style={{ fontFamily: "'Press Start 2P',monospace", fontSize: 10, color: "#fff", letterSpacing: 4, lineHeight: 1.4 }}>STAKED</div>
            <div style={{ fontFamily: "'Press Start 2P',monospace", fontSize: 10, color: "#39ff14", letterSpacing: 4, textShadow: "0 0 12px #39ff1488" }}>APE</div>
          </div>
        </a>
        <h1 style={{ fontSize: 22, margin: 0, color: C.gold, letterSpacing: 6, textShadow: `0 0 20px ${C.gold}66` }}>DOGEDROPZ</h1>
        <div style={{ fontSize: 7, color: C.cyan, marginTop: 4, letterSpacing: 3 }}>DROP THE COIN. WIN DOGE.</div>
      </div>

      {/* Status bar */}
      <div style={{ background: C.panel, border: `1px solid ${C.gold}33`, borderRadius: 10, padding: "8px 12px", marginBottom: 8, display: "flex", gap: 10, alignItems: "center", width: CANVAS_W, maxWidth: "100%", boxSizing: "border-box", justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 5, color: "#444", marginBottom: 2 }}>WALLET {user.verified ? "✅" : "⚠️"}</div>
            <div style={{ fontSize: 7, color: C.green, fontFamily: "monospace" }}>{user.walletAddress.slice(0, 6)}...{user.walletAddress.slice(-4)}</div>
          </div>
          <div style={{ width: 1, height: 28, background: "#333" }} />
          <div>
            <div style={{ fontSize: 5, color: "#444", marginBottom: 2 }}>BALANCE</div>
            <Glow color={C.gold} size={10}>{balance.toFixed(2)}</Glow>
          </div>
          {result && <>
            <div style={{ width: 1, height: 28, background: "#333" }} />
            <div>
              <div style={{ fontSize: 5, color: "#444", marginBottom: 2 }}>LAST WIN</div>
              <Glow color={result.won >= betAmount ? C.green : "#888"} size={10}>{result.won.toFixed(2)}</Glow>
            </div>
          </>}
        </div>
        <button onClick={() => setWalletOpen(true)} style={{ padding: "8px 12px", fontSize: 7, fontFamily: "inherit", background: C.gold + "22", border: `1px solid ${C.gold}66`, color: C.gold, borderRadius: 6, cursor: "pointer" }}>WALLET</button>
      </div>

      {/* Canvas */}
      <canvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H}
        style={{ border: `1px solid ${C.gold}44`, borderRadius: 8, boxShadow: `0 0 40px ${C.gold}11`, maxWidth: "100%", touchAction: "none", cursor: "crosshair" }}
      />

      {/* Message */}
      {message && (
        <div style={{ marginTop: 6, padding: "5px 16px", borderRadius: 6, textAlign: "center", fontSize: 9,
          color: message.startsWith("❌") ? C.red : result?.mult >= 5 ? C.gold : result?.mult >= 1 ? C.green : "#888",
          background: "#ffffff08", border: "1px solid #ffffff11", width: CANVAS_W, maxWidth: "100%", boxSizing: "border-box" }}>
          {message}
          {result?.mult >= 3 && (
            <a href="https://stakedape.com" target="_blank" style={{ textDecoration: "none", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 6 }}>
              <span style={{ fontSize: 16 }}>🦧</span>
              <div>
                <span style={{ fontFamily: "'Press Start 2P',monospace", fontSize: 7, color: "#fff", letterSpacing: 2 }}>STAKED</span>
                <span style={{ fontFamily: "'Press Start 2P',monospace", fontSize: 7, color: "#39ff14", letterSpacing: 2, marginLeft: 4, textShadow: "0 0 8px #39ff1488" }}>APE</span>
              </div>
            </a>
          )}
        </div>
      )}

      {/* Controls */}
      <div style={{ background: C.panel, border: "1px solid #ffffff11", borderRadius: 10, padding: 10, marginTop: 8, width: CANVAS_W, maxWidth: "100%", boxSizing: "border-box" }}>
        <div style={{ fontSize: 7, color: "#666", marginBottom: 6 }}>BET (DOGE)</div>
        <div style={{ display: "flex", gap: 4, marginBottom: 8, flexWrap: "wrap" }}>
          {bets.map(b => (
            <button key={b} onClick={() => setBetAmount(b)} disabled={false}
              style={{ background: betAmount === b ? C.gold + "33" : "#1a1a2e", border: `1px solid ${betAmount === b ? C.gold : "#333"}`, color: betAmount === b ? C.gold : "#888", padding: "6px 10px", borderRadius: 4, cursor: "pointer", fontFamily: "inherit", fontSize: 8 }}>{b}</button>
          ))}
        </div>
        <button onClick={dropBall} disabled={!canDrop}
          style={{ width: "100%", padding: "14px 0", background: canDrop ? `linear-gradient(135deg, ${C.gold}, ${C.goldLight})` : "#333", border: "none", borderRadius: 8, cursor: canDrop ? "pointer" : "not-allowed", fontFamily: "inherit", fontSize: 12, color: C.bg, fontWeight: "bold", letterSpacing: 4, boxShadow: canDrop ? `0 4px 20px ${C.gold}44` : "none" }}>
          {balance === 0 ? "DEPOSIT TO PLAY" : activeBalls > 0 ? `DROP (${activeBalls} ACTIVE)` : "DROP"}
        </button>
        <div style={{ textAlign: "center", fontSize: 6, color: "#555", marginTop: 4 }}>{canDrop ? "move mouse to aim · or press SPACEBAR" : ""}</div>
      </div>

      {/* History */}
      {history.length > 0 && (
        <div style={{ background: C.panel, border: "1px solid #ffffff11", borderRadius: 10, padding: 10, marginTop: 8, width: CANVAS_W, maxWidth: "100%", boxSizing: "border-box" }}>
          <div style={{ fontSize: 7, color: "#666", marginBottom: 6 }}>HISTORY</div>
          {history.slice(0, 8).map((h, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", borderBottom: "1px solid #1a1a2e", fontSize: 7 }}>
              <span style={{ color: "#555" }}>{h.mult}x</span>
              <span style={{ color: h.won >= h.bet ? C.green : C.red }}>{h.won >= h.bet ? "+" : ""}{(h.won - h.bet).toFixed(2)} DOGE</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #1a1a2e", textAlign: "center", width: CANVAS_W, maxWidth: "100%" }}>
        <a href="https://stakedape.com" target="_blank" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
          <span style={{ fontSize: 16 }}>🦧</span>
          <span style={{ fontFamily: "'Press Start 2P',monospace", fontSize: 7, color: "#fff", letterSpacing: 2 }}>STAKED</span>
          <span style={{ fontFamily: "'Press Start 2P',monospace", fontSize: 7, color: "#39ff14", letterSpacing: 2, textShadow: "0 0 8px #39ff1433" }}>APE</span>
        </a>
        <div style={{ fontSize: 6, color: "#333", letterSpacing: 2 }}>DOGEDROPZ · DOGEBRIX · DOGEGEMZ</div>
        <div style={{ fontSize: 5, color: "#222", letterSpacing: 2, marginTop: 2 }}>for entertainment purposes · 18+</div>
      </div>
    </div>
  );
}
